<template>
  <v-slide-x-transition>
    <v-list-tile
      v-if="showWidget"
      :disabled="disabled"
      class="list-tile-min-height list-tile-auto-height"
      data-cy="hard-range-warning-list-tile"
    >
      <v-icon
        :color="iconColor"
        v-text="icon"
      />
      <v-subheader class="auto-height full-width">
        <v-layout align-center>
          <div>
            <div v-if="title">
              <strong :class="[textColor]">{{ title }}</strong>
            </div>
            <div v-if="hardStart && !hardExpire">
              {{ $t('contentHardStartsOn') }}
              <span :class="[textColor]">{{
                hardStart | dayjs('DD.MM.YYYY')
              }}</span>
            </div>
            <div v-if="!hardStart && hardExpire">
              {{ $t('contentHardExpiresOn') }}
              <span :class="[textColor]">{{
                hardExpireOffset
                  ? subtractOneDayFromDate(hardExpire)
                  : hardExpire | dayjs('DD.MM.YYYY')
              }}</span>
            </div>
            <div
              v-if="hardStart && hardExpire"
              data-cy="content-hard-range-is"
            >
              {{ $t('contentHardRangeIs') }}
              <span
                class="nowrap"
                :class="[textColor]"
              >
                {{ hardStart | dayjs('DD.MM.YYYY') }} -
                {{
                  hardExpireOffset
                    ? subtractOneDayFromDate(hardExpire)
                    : hardExpire | dayjs('DD.MM.YYYY')
                }}
              </span>
            </div>
            <div
              v-if="chosenPeriodFeedback"
              class="caption"
              :class="[textColor]"
            >
              {{ contentValidityStatus }}
            </div>
          </div>
          <slot name="extra" />
        </v-layout>
      </v-subheader>
    </v-list-tile>
  </v-slide-x-transition>
</template>

<script>
import timing from '@/mixins/timing.js';

export default {
  mixins: [timing],
  props: {
    title: {
      type: String,
      default: '',
    },
    hardStart: {
      type: String,
      default: '',
    },
    hardExpire: {
      type: String,
      default: '',
    },
    hardExpireOffset: {
      type: Boolean,
      default: true,
    },
    chosenStartdate: {
      type: String,
      default: null,
    },
    chosenStopdate: {
      type: String,
      default: null,
    },
    /** Whether to dynamically adapt icon/colors/text based on chosenStart|Stopdate. */
    chosenPeriodFeedback: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Whether to hide the widget when chosen period is fully compatible with hard range. */
    hideWhenFullyCompatible: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    showWidget() {
      if (this.hardRangeFullyValid && this.hideWhenFullyCompatible) {
        return false;
      }
      const hasHardRangeDefined = this.hardStart || this.hardExpire;
      return hasHardRangeDefined;
    },
    formattedHardStart() {
      if (!this.hardStart) {
        return this.hardStart;
      }
      return this.formatDate(this.hardStart);
    },
    formattedHardExpire() {
      if (!this.hardExpire) {
        return this.hardExpire;
      }
      return this.formatDate(this.hardExpire);
    },
    chosenStartdateOrToday() {
      return this.chosenStartdate || this.formatDate(new Date());
    },
    chosenStopdateOrForever() {
      const forever = new Date();
      forever.setDate(forever.getDate() + 365 * 1000);
      return this.chosenStopdate || this.formatDate(forever);
    },
    startDateBeforeHardStart() {
      return (
        this.formattedHardStart &&
        this._isBefore(this.chosenStartdateOrToday, this.formattedHardStart)
      );
    },
    startDateAfterHardExpire() {
      return (
        this.formattedHardExpire &&
        this._isAfterOrSameAs(
          this.chosenStartdateOrToday,
          this.formattedHardExpire
        )
      );
    },
    startDateInHardRange() {
      if (this.hardStart && this.startDateBeforeHardStart) {
        return false;
      }
      if (this.hardExpire && this.startDateAfterHardExpire) {
        return false;
      }
      return true;
    },
    stopDateBeforeHardStart() {
      return (
        this.formattedHardStart &&
        this._isBefore(this.chosenStopdateOrForever, this.formattedHardStart)
      );
    },
    stopDateAfterHardExpire() {
      return (
        this.formattedHardExpire &&
        this._isAfterOrSameAs(
          this.chosenStopdateOrForever,
          this.formattedHardExpire
        )
      );
    },
    stopDateInHardRange() {
      if (this.hardStart && this.stopDateBeforeHardStart) {
        return false;
      }
      if (this.hardExpire && this.stopDateAfterHardExpire) {
        return false;
      }
      return true;
    },
    /**
     * Whether the chosen dates will be used 1:1 during activation.
     */
    hardRangeFullyValid() {
      return this.startDateInHardRange && this.stopDateInHardRange;
    },
    /**
     * Whether the chosen dates are entirely invalid with the hard range.
     *
     * The content will not be activated with these settings.
     */
    hardRangeInvalid() {
      if (this.hardExpire && this.startDateAfterHardExpire) {
        return true;
      }
      if (this.hardStart && this.stopDateBeforeHardStart) {
        return true;
      }
      return false;
    },
    contentValidityStatus() {
      if (this.hardRangeFullyValid) {
        return this.$t('contentHardRangeFullyValid');
      } else if (this.hardRangeInvalid) {
        return this.$t('contentHardRangeInvalid');
      }
      return this.$t('contentHardRangePartiallyValid');
    },
    color() {
      if (this.chosenPeriodFeedback) {
        if (this.hardRangeFullyValid) {
          return 'success';
        } else if (this.hardRangeInvalid) {
          return 'error';
        }
      }
      // This means that some adaptation will be done e.g. by moving
      // either the chosen startdate or stopdate to match the hard range.
      return 'warning';
    },
    icon() {
      return 'date_range';
    },
    iconColor() {
      return `${this.color}`;
    },
    textColor() {
      return `${this.color}--text`;
    },
  },
  watch: {
    /** Tell parent component whether a change (in)validated this. */
    hardRangeInvalid: function (hardRangeInvalid) {
      this.$emit('invalid', hardRangeInvalid);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/HardRangeWarningListTile.vue